import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import type {
  AdCreativeVideoTextInspectionEvidenceView,
  AdCreativeVideoTextInspectionLibraryView,
  AdCreativeVideoTextInspectionRegionInput,
} from '../../services/AdCreativeLocalizationApiContract';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { AD_CREATIVE_VIDEO_TEXT_INSPECTION } from '../../selectors';
import { exactSourceVideoAssetWithGeometry } from './videoLocalizationSource';

const MAX_REGIONS = 30;

type VideoTextInspectionClient = Pick<
  AdCreativeApiClient,
  'listVideoTextInspections' | 'reviewVideoTextInspection'
>;

interface VideoTextRegionDraft {
  regionId: string;
  sourceText: string;
  targetText: string;
  startSeconds: string;
  endSeconds: string;
  leftPercent: string;
  topPercent: string;
  widthPercent: string;
  heightPercent: string;
}

export interface AdCreativeVideoTextInspectionSelection {
  evidence: AdCreativeVideoTextInspectionEvidenceView;
  replacements: Array<{ regionId: string; targetText: string }>;
}

export interface VideoTextInspectionPanelProps {
  projectId: string;
  item: AdCreativeItem;
  slotKey: string;
  targetLocale: string;
  client: VideoTextInspectionClient;
  disabled?: boolean;
  onSelectionChange: (selection: AdCreativeVideoTextInspectionSelection | null) => void;
}

export function VideoTextInspectionPanel({
  projectId,
  item,
  slotKey,
  targetLocale,
  client,
  disabled = false,
  onSelectionChange,
}: VideoTextInspectionPanelProps): React.ReactElement {
  const sourceAsset = useMemo(() => exactSourceVideoAssetWithGeometry(item, slotKey), [item, slotKey]);
  const [regions, setRegions] = useState<VideoTextRegionDraft[]>([emptyRegion(1)]);
  const [confirmed, setConfirmed] = useState(false);
  const [evidence, setEvidence] = useState<AdCreativeVideoTextInspectionEvidenceView | null>(null);
  const [library, setLibrary] = useState<AdCreativeVideoTextInspectionLibraryView | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const identity = `${projectId}:${item.id}:${item.sourceRevision}:${slotKey}:${targetLocale}:${sourceAsset?.assetId ?? ''}:${sourceAsset?.sha256 ?? ''}`;
  const identityRef = useRef(identity);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const evidenceRef = useRef(evidence);
  identityRef.current = identity;
  onSelectionChangeRef.current = onSelectionChange;
  evidenceRef.current = evidence;

  useEffect(() => {
    let active = true;
    setRegions([emptyRegion(1)]);
    setConfirmed(false);
    setEvidence(null);
    setLibrary(null);
    setLibraryLoading(Boolean(sourceAsset));
    setLoadingOlder(false);
    setBusy(false);
    setError(null);
    setStatus(null);
    onSelectionChangeRef.current(null);
    if (sourceAsset) {
      void client.listVideoTextInspections({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        limit: 10,
      }).then((response) => {
        if (!response.success || !response.data?.library) {
          throw new Error(response.error || 'Saved source text maps could not be loaded.');
        }
        assertExactLibrary(response.data.library, item, slotKey, sourceAsset);
        if (active && identityRef.current === identity) setLibrary(response.data.library);
      }).catch((nextError) => {
        if (active && identityRef.current === identity) {
          setError(nextError instanceof Error ? nextError.message : String(nextError));
        }
      }).finally(() => {
        if (active && identityRef.current === identity) setLibraryLoading(false);
      });
    }
    return () => {
      active = false;
    };
  }, [client, identity, item, projectId, slotKey, sourceAsset]);

  const selectExistingEvidence = (selected: AdCreativeVideoTextInspectionEvidenceView): void => {
    if (!sourceAsset) return;
    assertEvidenceMatchesCurrentSource(selected, item, slotKey, sourceAsset);
    const drafts = selected.regions.map(draftFromEvidenceRegion);
    setRegions(drafts);
    setConfirmed(true);
    setEvidence(selected);
    evidenceRef.current = selected;
    setError(null);
    setStatus('Saved source text map selected. Enter every target replacement before preflight.');
    onSelectionChangeRef.current(selectionFromDraft(selected, drafts));
  };

  const loadOlder = async (): Promise<void> => {
    if (!sourceAsset || !library?.nextCursor) return;
    const expectedIdentity = identity;
    setLoadingOlder(true);
    setError(null);
    try {
      const response = await client.listVideoTextInspections({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        limit: 10,
        cursor: library.nextCursor,
      });
      if (!response.success || !response.data?.library) {
        throw new Error(response.error || 'Older source text maps could not be loaded.');
      }
      assertExactLibrary(response.data.library, item, slotKey, sourceAsset);
      if (identityRef.current !== expectedIdentity) return;
      setLibrary((current) => current ? {
        ...response.data!.library,
        evidence: Array.from(new Map(
          [...current.evidence, ...response.data!.library.evidence]
            .map((entry) => [entry.evidenceId, entry]),
        ).values()),
      } : response.data!.library);
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (identityRef.current === expectedIdentity) setLoadingOlder(false);
    }
  };

  const invalidateSourceReview = (): void => {
    setConfirmed(false);
    setEvidence(null);
    evidenceRef.current = null;
    setStatus(null);
    setError(null);
    onSelectionChangeRef.current(null);
  };

  const updateSourceField = (
    regionId: string,
    field: Exclude<keyof VideoTextRegionDraft, 'regionId' | 'targetText'>,
    value: string,
  ): void => {
    setRegions((current) => current.map((region) => (
      region.regionId === regionId ? { ...region, [field]: value } : region
    )));
    invalidateSourceReview();
  };

  const updateTargetText = (regionId: string, targetText: string): void => {
    setError(null);
    setRegions((current) => {
      const next = current.map((region) => (
        region.regionId === regionId ? { ...region, targetText } : region
      ));
      if (evidenceRef.current) {
        onSelectionChangeRef.current(selectionFromDraft(evidenceRef.current, next));
      }
      return next;
    });
  };

  const addRegion = (): void => {
    setRegions((current) => {
      if (current.length >= MAX_REGIONS) return current;
      const nextOrdinal = current.reduce((maximum, region) => (
        Math.max(maximum, Number(region.regionId.replace(/^region_/, '')) || 0)
      ), 0) + 1;
      return [...current, emptyRegion(nextOrdinal)];
    });
    invalidateSourceReview();
  };

  const removeRegion = (regionId: string): void => {
    setRegions((current) => current.length <= 1
      ? current
      : current.filter((region) => region.regionId !== regionId));
    invalidateSourceReview();
  };

  const save = async (): Promise<void> => {
    const expectedIdentity = identity;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video.');
      if (!confirmed) throw new Error('Confirm that you reviewed the exact source playback and every marked region.');
      const normalizedRegions = normalizeDraftRegions(regions, sourceAsset.durationMs!);
      const response = await client.reviewVideoTextInspection({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        regions: normalizedRegions,
        confirmedExactSourcePlayback: true,
      });
      if (!response.success || !response.data?.evidence) {
        throw new Error(response.error || 'The exact video text map could not be saved.');
      }
      assertExactEvidence(
        response.data.evidence,
        item,
        slotKey,
        sourceAsset.sha256!,
        normalizedRegions,
      );
      if (identityRef.current !== expectedIdentity) return;
      setEvidence(response.data.evidence);
      evidenceRef.current = response.data.evidence;
      setLibrary((current) => current ? {
        ...current,
        evidence: [
          response.data!.evidence,
          ...current.evidence.filter((entry) => entry.evidenceId !== response.data!.evidence.evidenceId),
        ],
      } : current);
      onSelectionChangeRef.current(selectionFromDraft(response.data.evidence, regions));
      setStatus(response.data.reused
        ? 'The existing immutable text map was verified and reused.'
        : 'Immutable source text map saved from server-resampled exact frames.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
        setEvidence(null);
        evidenceRef.current = null;
        onSelectionChangeRef.current(null);
      }
    } finally {
      if (identityRef.current === expectedIdentity) setBusy(false);
    }
  };

  if (!sourceAsset) {
    return (
      <Alert severity="warning" data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.panel}>
        An exact durable source video is required before on-screen text can be mapped.
      </Alert>
    );
  }

  return (
    <Box
      data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.panel}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Exact source video text map</Typography>
          <Typography variant="caption" color="text.secondary">
            Play this exact source, enter only text that is visibly present, and mark where and when it appears. Saving is a manual evidence action and uses no AI credits.
          </Typography>
        </Box>
        <Box
          component="video"
          controls
          preload="metadata"
          src={sourceAsset.original}
          data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.sourceVideo}
          sx={{ width: '100%', maxHeight: 360, borderRadius: 1, bgcolor: 'black' }}
        />
        <Alert severity="info">
          The text map can be prepared now. Paid visual replacement remains fail-closed until a controlled renderer and exact output OCR verifier are enabled.
        </Alert>
        {libraryLoading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={14} />
            <Typography variant="caption">Loading saved source text maps…</Typography>
          </Stack>
        )}
        {!libraryLoading && library && library.evidence.length > 0 && (
          <Box
            aria-label="Saved source text maps"
            data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.history}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
          >
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Saved exact source maps
              </Typography>
              {library.evidence.map((entry) => (
                <Button
                  key={entry.evidenceId}
                  size="small"
                  variant={evidence?.evidenceId === entry.evidenceId ? 'contained' : 'outlined'}
                  disabled={disabled || busy || loadingOlder}
                  data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.useEvidence(entry.evidenceId)}
                  onClick={() => selectExistingEvidence(entry)}
                >
                  Use {entry.regions.length}-region map · {new Date(entry.createdAt).toLocaleString()}
                </Button>
              ))}
              {library.nextCursor && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={disabled || busy || loadingOlder}
                  data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.loadMore}
                  startIcon={loadingOlder ? <CircularProgress size={12} color="inherit" /> : undefined}
                  onClick={() => void loadOlder()}
                >
                  Load older maps
                </Button>
              )}
            </Stack>
          </Box>
        )}
        {regions.map((region, index) => (
          <Box
            key={region.regionId}
            data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.region(region.regionId)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Visible region {index + 1}</Typography>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  disabled={disabled || busy || regions.length === 1}
                  data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.removeRegion(region.regionId)}
                  onClick={() => removeRegion(region.regionId)}
                >
                  Remove
                </Button>
              </Stack>
              <StyledTextField
                fullWidth
                size="small"
                label="Exact source text"
                value={region.sourceText}
                disabled={disabled || busy}
                data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.sourceText(region.regionId)}
                onChange={(value) => updateSourceField(region.regionId, 'sourceText', String(value))}
              />
              <StyledTextField
                fullWidth
                size="small"
                label={`Exact ${targetLocale} replacement`}
                value={region.targetText}
                disabled={disabled || busy}
                helperText="Required before checking the on-screen replacement capability. Segmently will not invent this text."
                data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.targetText(region.regionId)}
                onChange={(value) => updateTargetText(region.regionId, String(value))}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                <StyledTextField
                  size="small"
                  label="Start (seconds)"
                  value={region.startSeconds}
                  disabled={disabled || busy}
                  data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.startSeconds(region.regionId)}
                  onChange={(value) => updateSourceField(region.regionId, 'startSeconds', String(value))}
                />
                <StyledTextField
                  size="small"
                  label="End (seconds)"
                  value={region.endSeconds}
                  disabled={disabled || busy}
                  data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.endSeconds(region.regionId)}
                  onChange={(value) => updateSourceField(region.regionId, 'endSeconds', String(value))}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">Region as percentages of the video frame</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                {([
                  ['leftPercent', 'Left %', AD_CREATIVE_VIDEO_TEXT_INSPECTION.leftPercent],
                  ['topPercent', 'Top %', AD_CREATIVE_VIDEO_TEXT_INSPECTION.topPercent],
                  ['widthPercent', 'Width %', AD_CREATIVE_VIDEO_TEXT_INSPECTION.widthPercent],
                  ['heightPercent', 'Height %', AD_CREATIVE_VIDEO_TEXT_INSPECTION.heightPercent],
                ] as const).map(([field, label, testId]) => (
                  <StyledTextField
                    key={field}
                    size="small"
                    label={label}
                    value={region[field]}
                    disabled={disabled || busy}
                    data-testid={testId(region.regionId)}
                    onChange={(value) => updateSourceField(region.regionId, field, String(value))}
                  />
                ))}
              </Box>
            </Stack>
          </Box>
        ))}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          disabled={disabled || busy || regions.length >= MAX_REGIONS}
          data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.addRegion}
          onClick={addRegion}
        >
          Add visible region ({regions.length}/{MAX_REGIONS})
        </Button>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={confirmed}
              disabled={disabled || busy}
              data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.confirmation}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
          )}
          label="I played the exact source video and reviewed every text, time range, and frame region"
          sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'caption', pt: 0.4 } }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={disabled || busy || !confirmed}
          data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.save}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={() => void save()}
        >
          Save exact source text map
        </Button>
        {evidence && (
          <Alert severity="success" data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.evidence}>
            <Stack spacing={0.5}>
              <Typography variant="caption">Immutable evidence is bound to this exact source video.</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${evidence.regions.length} regions`} />
                <Chip size="small" label={`${evidence.proof.frameCount} proof frames`} />
                <Chip size="small" label={evidence.evidenceId} sx={{ maxWidth: '100%' }} />
              </Stack>
            </Stack>
          </Alert>
        )}
        {error && <Alert severity="error" data-testid={AD_CREATIVE_VIDEO_TEXT_INSPECTION.error}>{error}</Alert>}
        {status && <Alert severity="success">{status}</Alert>}
      </Stack>
    </Box>
  );
}

function emptyRegion(ordinal: number): VideoTextRegionDraft {
  return {
    regionId: `region_${String(ordinal).padStart(2, '0')}`,
    sourceText: '',
    targetText: '',
    startSeconds: '0',
    endSeconds: '1',
    leftPercent: '10',
    topPercent: '10',
    widthPercent: '80',
    heightPercent: '20',
  };
}

function draftFromEvidenceRegion(
  region: AdCreativeVideoTextInspectionEvidenceView['regions'][number],
): VideoTextRegionDraft {
  return {
    regionId: region.regionId,
    sourceText: region.sourceText,
    targetText: '',
    startSeconds: formatScaled(region.startMs, 1_000),
    endSeconds: formatScaled(region.endMs, 1_000),
    leftPercent: formatScaled(region.bounds.leftBps, 100),
    topPercent: formatScaled(region.bounds.topBps, 100),
    widthPercent: formatScaled(region.bounds.rightBps - region.bounds.leftBps, 100),
    heightPercent: formatScaled(region.bounds.bottomBps - region.bounds.topBps, 100),
  };
}

function formatScaled(value: number, divisor: number): string {
  return String(Number((value / divisor).toFixed(3)));
}

function normalizeDraftRegions(
  drafts: VideoTextRegionDraft[],
  durationMs: number,
): AdCreativeVideoTextInspectionRegionInput[] {
  if (drafts.length === 0 || drafts.length > MAX_REGIONS) {
    throw new Error(`Enter between 1 and ${MAX_REGIONS} visible text regions.`);
  }
  return drafts.map((draft, index) => {
    const sourceText = draft.sourceText.trim();
    if (!sourceText) throw new Error(`Visible region ${index + 1} requires exact source text.`);
    const startMs = secondsToMs(draft.startSeconds, `Visible region ${index + 1} start`);
    const endMs = secondsToMs(draft.endSeconds, `Visible region ${index + 1} end`);
    if (endMs <= startMs || endMs > durationMs) {
      throw new Error(`Visible region ${index + 1} must have a positive time range inside the source video.`);
    }
    const leftBps = percentToBps(draft.leftPercent, `Visible region ${index + 1} left`);
    const topBps = percentToBps(draft.topPercent, `Visible region ${index + 1} top`);
    const widthBps = percentToBps(draft.widthPercent, `Visible region ${index + 1} width`);
    const heightBps = percentToBps(draft.heightPercent, `Visible region ${index + 1} height`);
    const rightBps = leftBps + widthBps;
    const bottomBps = topBps + heightBps;
    if (widthBps <= 0 || heightBps <= 0 || rightBps > 10_000 || bottomBps > 10_000) {
      throw new Error(`Visible region ${index + 1} must fit fully inside the video frame.`);
    }
    return {
      regionId: draft.regionId,
      sourceText,
      startMs,
      endMs,
      bounds: { leftBps, topBps, rightBps, bottomBps },
    };
  });
}

function secondsToMs(value: string, field: string): number {
  const seconds = Number(value);
  const milliseconds = Math.round(seconds * 1_000);
  if (!Number.isFinite(seconds) || seconds < 0 || !Number.isSafeInteger(milliseconds)) {
    throw new Error(`${field} must be a valid non-negative number of seconds.`);
  }
  return milliseconds;
}

function percentToBps(value: string, field: string): number {
  const percent = Number(value);
  const basisPoints = Math.round(percent * 100);
  if (!Number.isFinite(percent)
    || percent < 0
    || percent > 100
    || !Number.isSafeInteger(basisPoints)) {
    throw new Error(`${field} must be between 0 and 100 percent.`);
  }
  return basisPoints;
}

function selectionFromDraft(
  evidence: AdCreativeVideoTextInspectionEvidenceView,
  drafts: VideoTextRegionDraft[],
): AdCreativeVideoTextInspectionSelection {
  return {
    evidence,
    replacements: drafts.map((draft) => ({
      regionId: draft.regionId,
      targetText: draft.targetText.trim(),
    })),
  };
}

function assertExactEvidence(
  evidence: AdCreativeVideoTextInspectionEvidenceView,
  item: AdCreativeItem,
  slotKey: string,
  sourceSha256: string,
  regions: AdCreativeVideoTextInspectionRegionInput[],
): void {
  if (evidence.immutable !== true
    || evidence.status !== 'approved'
    || evidence.creativeItemId !== item.id
    || evidence.slotKey !== slotKey
    || evidence.source.historicalSourceRevision !== item.sourceRevision
    || evidence.source.sourceSha256 !== sourceSha256
    || !/^advideotext_[a-f0-9]{40}$/.test(evidence.evidenceId)
    || !/^[a-f0-9]{64}$/.test(evidence.contentHash)
    || !/^[a-f0-9]{64}$/.test(evidence.source.sourceComponentHash)
    || !/^[a-f0-9]{64}$/.test(evidence.proof.sampleSetHash)
    || evidence.proof.frameCount < 1
    || evidence.regions.length !== regions.length
    || evidence.regions.some((region, index) => (
      region.regionId !== regions[index].regionId
      || region.sourceText !== regions[index].sourceText
      || region.startMs !== regions[index].startMs
      || region.endMs !== regions[index].endMs
      || JSON.stringify(region.bounds) !== JSON.stringify(regions[index].bounds)
      || !Array.isArray(region.sampleIds)
      || region.sampleIds.length === 0
    ))) {
    throw new Error('Video text evidence returned mismatched exact-source lineage or regions.');
  }
}

function assertExactLibrary(
  library: AdCreativeVideoTextInspectionLibraryView,
  item: AdCreativeItem,
  slotKey: string,
  sourceAsset: NonNullable<ReturnType<typeof exactSourceVideoAssetWithGeometry>>,
): void {
  if (library.creativeItemId !== item.id
    || library.slotKey !== slotKey
    || library.sourceRevision !== item.sourceRevision
    || library.sourceAssetId !== sourceAsset.assetId
    || library.sourceAssetSha256 !== sourceAsset.sha256
    || !/^[a-f0-9]{64}$/.test(library.sourceComponentHash)
    || !Array.isArray(library.evidence)
    || library.evidence.length > MAX_REGIONS
    || (library.nextCursor !== undefined && !library.truncated)) {
    throw new Error('Saved video text maps do not match the exact current source video lineage.');
  }
  library.evidence.forEach((entry) => {
    assertEvidenceMatchesCurrentSource(entry, item, slotKey, sourceAsset);
    if (entry.source.sourceComponentHash !== library.sourceComponentHash) {
      throw new Error('Saved video text maps do not match the exact current source video lineage.');
    }
  });
}

function assertEvidenceMatchesCurrentSource(
  evidence: AdCreativeVideoTextInspectionEvidenceView,
  item: AdCreativeItem,
  slotKey: string,
  sourceAsset: NonNullable<ReturnType<typeof exactSourceVideoAssetWithGeometry>>,
): void {
  if (evidence.immutable !== true
    || evidence.status !== 'approved'
    || evidence.creativeItemId !== item.id
    || evidence.slotKey !== slotKey
    || evidence.source.sourceAssetId !== sourceAsset.assetId
    || evidence.source.sourceSha256 !== sourceAsset.sha256
    || evidence.source.mimeType !== sourceAsset.mimeType
    || evidence.source.width !== sourceAsset.width
    || evidence.source.height !== sourceAsset.height
    || evidence.source.durationMs !== sourceAsset.durationMs
    || !/^advideotext_[a-f0-9]{40}$/.test(evidence.evidenceId)
    || !/^[a-f0-9]{64}$/.test(evidence.contentHash)
    || !/^[a-f0-9]{64}$/.test(evidence.source.sourceComponentHash)
    || evidence.regions.length === 0
    || evidence.regions.length > MAX_REGIONS) {
    throw new Error('Saved video text maps do not match the exact current source video lineage.');
  }
}
